import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import DeliveryNote from '../components/Invoice/DeliveryNote';
import { t } from '../i18n';

export default function DeliveryNoteAddPage() {
  const deliveryNoteRef = useRef();
  const { id } = useParams();
  
  // Check if we're in edit mode based on URL parameters
  const isEditMode = !!id;
  
  // Local state for checkboxes to ensure they update
  const [settings, setSettings] = useState({
    reklamacijeEnabled: false,
    footerEnabled: true,
    KomercijalistEnabled: true,
    PodignalEnabled: true,
    voziloEnabled: true
  });

  // Local state for view mode
  const [viewMode, setViewMode] = useState('advanced');

  const handlePrint = () => {
    if (deliveryNoteRef.current) {
      deliveryNoteRef.current.handlePrint();
    }
  };

  const handleSave = () => {
    if (deliveryNoteRef.current) {
      deliveryNoteRef.current.handleSave();
    }
  };

  // const handleSaveDraft = () => {
  //   if (deliveryNoteRef.current) {
  //     deliveryNoteRef.current.handleSaveDraft();
  //   }
  // };

  const handleAddRow = () => {
    if (deliveryNoteRef.current) {
      deliveryNoteRef.current.handleAddRow();
    }
  };

  const handleToggleSetting = (settingName) => {
    setSettings(prev => ({
      ...prev,
      [settingName]: !prev[settingName]
    }));
    
    // Also call the function on the generator
    if (deliveryNoteRef.current) {
      switch (settingName) {
        case 'reklamacijeEnabled':
          deliveryNoteRef.current.toggleNote();
          break;
        case 'footerEnabled':
          deliveryNoteRef.current.toggleFooter();
          break;
        case 'KomercijalistEnabled':
          deliveryNoteRef.current.toggleKomercijalistEnabled();
          break;
        case 'PodignalEnabled':
          deliveryNoteRef.current.togglePodignalEnabled();
          break;
        case 'voziloEnabled':
          deliveryNoteRef.current.toggleVoziloEnabled();
          break;
      }
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (deliveryNoteRef.current) {
      deliveryNoteRef.current.setViewMode(mode);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 -m-8 p-8">
      <div className="flex w-full gap-6">
        {/* Main content area */}
        <div className="flex-1 p-4 bg-gray-200 rounded-lg shadow">
          <DeliveryNote ref={deliveryNoteRef} hideSidebar={true} editId={id} />
        </div>

        {/* Action buttons sidebar - outside of delivery note */}
        <div className="w-48 p-4 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col gap-3 h-fit">
          {/* Advanced Mode Toggle */}
          <div className="mb-2">
            <div className="text-xs text-gray-600 mb-1 font-medium text-center">{t('Напреден режим')}</div>
            <div className="flex bg-gray-200 rounded-lg p-1 shadow-sm border">
              <button
                onClick={() => handleViewModeChange('basic')}
                className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  viewMode === 'basic'
                    ? 'bg-red-500 text-white shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {t('ИСКЛ')}
              </button>
              <button
                onClick={() => handleViewModeChange('advanced')}
                className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  viewMode === 'advanced'
                    ? 'bg-green-500 text-white shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {t('ВКЛ')}
              </button>
            </div>
          </div>

          <hr className="my-2 border-gray-200" />

          <button
            onClick={handlePrint}
            className="w-full bg-white border border-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-100 text-sm font-medium"
          >
            {t('Печати')}
          </button>
          
          {/* <button
            onClick={handleSaveDraft}
            className="w-full bg-yellow-100 text-yellow-900 py-2 px-4 rounded hover:bg-yellow-200 text-sm font-medium"
          >
            Save and Create Invoice
          </button> */}
          
          <button
            onClick={handleSave}
            className="w-full bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-700 text-sm font-medium"
          >
            {t('Зачувај')}
          </button>

          <hr className="my-2 border-gray-200" />
          
          <div className="text-sm font-semibold text-gray-700 mb-2">{t('Подесувања')}</div>
          
          <button
            onClick={handleAddRow}
            className="w-full bg-gray-200 text-gray-800 text-sm py-2 px-4 rounded hover:bg-gray-300 font-medium"
          >
            {t('+ Додади ред')}
          </button>
          
          {viewMode === 'advanced' && (
            <>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleToggleSetting('reklamacijeEnabled')}
                  className={`w-full text-sm py-1 px-2 rounded bg-white text-gray-800 hover:bg-gray-100 border text-left ${
                    settings.reklamacijeEnabled ? 'border-green-600' : 'border-red-300'
                  }`}
                >
                  {settings.reklamacijeEnabled ? t('Напомена вкл.') : t('Напомена искл.')}
                </button>

                <button
                  onClick={() => handleToggleSetting('footerEnabled')}
                  className={`w-full text-sm py-1 px-2 rounded bg-white text-gray-800 hover:bg-gray-100 border text-left ${
                    settings.footerEnabled ? 'border-green-600' : 'border-red-300'
                  }`}
                >
                  {settings.footerEnabled ? t('Подножје вкл.') : t('Подножје искл.')}
                </button>
              </div>

              <div className="text-xs font-semibold text-gray-600 mb-2 mt-3">{t('Прикажи полиња')}</div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.KomercijalistEnabled}
                    onChange={() => handleToggleSetting('KomercijalistEnabled')}
                    className={`form-checkbox h-4 w-4 text-green-600 border ${
                      settings.KomercijalistEnabled ? 'border-green-600' : 'border-red-300'
                    }`}
                  />
                  <label className="ml-2 text-sm text-gray-800">{t('Комерцијалист')}</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.PodignalEnabled}
                    onChange={() => handleToggleSetting('PodignalEnabled')}
                    className={`form-checkbox h-4 w-4 text-green-600 border ${
                      settings.PodignalEnabled ? 'border-green-600' : 'border-red-300'
                    }`}
                  />
                  <label className="ml-2 text-sm text-gray-800">{t('Подигнал')}</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.voziloEnabled}
                    onChange={() => handleToggleSetting('voziloEnabled')}
                    className={`form-checkbox h-4 w-4 text-green-600 border ${
                      settings.voziloEnabled ? 'border-green-600' : 'border-red-300'
                    }`}
                  />
                  <label className="ml-2 text-sm text-gray-800">{t('Возило')}</label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}